import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { teacherService, Teacher, PageResponse, TeacherSearchRequest, TeacherSearchFilters } from '../../services/teacherService';
import { profileService, Profile } from '../../services/profileService';
import { useRights } from '../../hooks/useRights';
import { useHasAnyActionRight, teacherActions } from '../../utils/actionRightsUtils';
import { ResourceType } from '../../services/billService';
import { useModal } from '../../context/ModalContext';
import { Lock, Receipt, Pencil, Trash2 } from 'lucide-react';
import '../../styles/index.css';
import './Teachers.css';
import TableActions from '../../components/TableActions';

const Teachers: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { schoolId } = useAuth();
  const { hasRight } = useRights();
  const { showConfirm, showAlert } = useModal();
  const hasAnyActionRight = useHasAnyActionRight(teacherActions());
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [totalElements, setTotalElements] = useState(0);
  const [isDeepSearch, setIsDeepSearch] = useState(false);
  const observerTarget = useRef<HTMLDivElement>(null);
  const [editingTeacher, setEditingTeacher] = useState<Teacher | null>(null);
  const [editForm, setEditForm] = useState({
    userName: '',
    profileId: '',
    firstName: '',
    lastName: '',
    enabled: true,
  });
  const [isCreating, setIsCreating] = useState(false);
  const [createForm, setCreateForm] = useState({
    userName: '',
    profileId: '',
    firstName: '',
    lastName: '',
    enabled: true,
  });
  const [resetPasswordTeacher, setResetPasswordTeacher] = useState<Teacher | null>(null);
  const [resetPasswordValue, setResetPasswordValue] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [filters, setFilters] = useState<TeacherSearchFilters>({
    userName: '',
    firstName: '',
    lastName: '',
    enabled: undefined,
  });

  useEffect(() => {
    loadTeachers(0, true);
  }, []);

  const teacherProfiles = useMemo(() => profiles.filter(p => p.type === 'TEACHER'), [profiles]);

  useEffect(() => {
    if (isCreating && teacherProfiles.length > 0 && !createForm.profileId) {
      setCreateForm(prev => ({ ...prev, profileId: teacherProfiles[0].id }));
    }
  }, [isCreating, teacherProfiles, createForm.profileId]);

  const loadProfiles = async () => {
    // Only load profiles if user has create or update permissions
    if (!hasRight('teachers:c') && !hasRight('teachers:u')) {
      return;
    }
    try {
      const data = await profileService.getAllProfiles();
      setProfiles(data);
    } catch (e: any) {
      console.error('Failed to load profiles:', e);
    }
  };

  const loadTeachers = async (page: number = 0, reset: boolean = true) => {
    try {
      if (reset) {
        setLoading(true);
        setCurrentPage(0);
        setIsDeepSearch(false);
      } else {
        setLoadingMore(true);
      }

      const searchRequest: TeacherSearchRequest = {
        page,
        size: 100,
        filters: isDeepSearch ? filters : undefined,
      };

      const response: PageResponse<Teacher> = await teacherService.searchTeachers(searchRequest);

      if (reset) {
        setTeachers(response.content || []);
      } else {
        setTeachers(prev => [...(prev || []), ...(response.content || [])]);
      }

      setCurrentPage(response.page);
      setHasMore(response.hasNext);
      setTotalElements(response.totalElements);
      setError('');
    } catch (e: any) {
      setError(e.response?.data?.message || 'Failed to load teachers');
      console.error('Load teachers error:', e);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMoreTeachers = useCallback(() => {
    if (!loadingMore && hasMore && !loading) {
      setLoadingMore(true);
      const searchFilters: TeacherSearchFilters = {
        userName: filters.userName || undefined,
        firstName: filters.firstName || undefined,
        lastName: filters.lastName || undefined,
        enabled: filters.enabled !== undefined ? filters.enabled : undefined,
      };

      const searchRequest: TeacherSearchRequest = {
        page: currentPage + 1,
        size: 100,
        filters: isDeepSearch ? searchFilters : undefined,
      };

      teacherService.searchTeachers(searchRequest).then(response => {
        setTeachers(prev => [...(prev || []), ...(response.content || [])]);
        setCurrentPage(response.page);
        setHasMore(response.hasNext);
        setTotalElements(response.totalElements);
        setLoadingMore(false);
      }).catch(e => {
        setError(e.response?.data?.message || 'Failed to load more teachers');
        setLoadingMore(false);
      });
    }
  }, [currentPage, hasMore, loadingMore, loading, isDeepSearch, filters]);

  const handleDeepSearch = async () => {
    try {
      setLoading(true);
      setIsDeepSearch(true);
      setCurrentPage(0);

      const searchFilters: TeacherSearchFilters = {
        userName: filters.userName || undefined,
        firstName: filters.firstName || undefined,
        lastName: filters.lastName || undefined,
        enabled: filters.enabled !== undefined ? filters.enabled : undefined,
      };

      const searchRequest: TeacherSearchRequest = {
        page: 0,
        size: 100,
        filters: searchFilters,
      };

      const response: PageResponse<Teacher> = await teacherService.searchTeachers(searchRequest);
      setTeachers(response.content || []);
      setCurrentPage(response.page);
      setHasMore(response.hasNext);
      setTotalElements(response.totalElements);
      setError('');
    } catch (e: any) {
      setError(e.response?.data?.message || 'Failed to search teachers');
      console.error('Deep search error:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const currentTarget = observerTarget.current;
    if (!currentTarget) return;

    const handleScroll = () => {
      const container = currentTarget;
      const scrollTop = container.scrollTop;
      const scrollHeight = container.scrollHeight;
      const clientHeight = container.clientHeight;

      if (scrollHeight - scrollTop - clientHeight < 100 && hasMore && !loadingMore && !loading) {
        loadMoreTeachers();
      }
    };

    currentTarget.addEventListener('scroll', handleScroll);

    return () => {
      currentTarget.removeEventListener('scroll', handleScroll);
    };
  }, [loadMoreTeachers, hasMore, loadingMore, loading]);

  const filteredTeachers = isDeepSearch ? (teachers || []) : (teachers || []).filter((teacher) => {
    if (filters.userName && !teacher.userName?.toLowerCase().includes(filters.userName.toLowerCase())) {
      return false;
    }
    if (filters.firstName && !teacher.firstName?.toLowerCase().includes(filters.firstName.toLowerCase())) {
      return false;
    }
    if (filters.lastName && !teacher.lastName?.toLowerCase().includes(filters.lastName.toLowerCase())) {
      return false;
    }
    if (filters.enabled !== undefined && teacher.enabled !== filters.enabled) {
      return false;
    }
    return true;
  });

  const handleFilterChange = (key: keyof TeacherSearchFilters, value: string | boolean | undefined) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setIsDeepSearch(false);
  };

  const clearFilters = () => {
    setFilters({
      userName: '',
      firstName: '',
      lastName: '',
      enabled: undefined,
    });
    setIsDeepSearch(false);
    loadTeachers(0, true);
  };

  const handleEdit = (teacher: Teacher) => {
    setEditingTeacher(teacher);
    setEditForm({
      userName: teacher.userName || '',
      profileId: teacher.profileId || '',
      firstName: teacher.firstName || '',
      lastName: teacher.lastName || '',
      enabled: teacher.enabled ?? true,
    });
    // Load profiles only when editing (if not already loaded)
    if (profiles.length === 0) {
      loadProfiles();
    }
  };

  const handleCancelEdit = () => {
    setEditingTeacher(null);
    setEditForm({
      userName: '',
      profileId: '',
      firstName: '',
      lastName: '',
      enabled: true,
    });
  };

  const handleCreate = () => {
    setIsCreating(true);
    setCreateForm({
      userName: '',
      profileId: '',
      firstName: '',
      lastName: '',
      enabled: true,
    });
    // Load profiles only when creating (if not already loaded)
    if (profiles.length === 0) {
      loadProfiles();
    }
  };

  const handleCancelCreate = () => {
    setIsCreating(false);
    setCreateForm({
      userName: '',
      profileId: '',
      firstName: '',
      lastName: '',
      enabled: true,
    });
  };

  const handleSaveCreate = async () => {
    if (!createForm.userName.trim()) {
      setError(t('teachers.userNameRequired'));
      return;
    }
    if (!createForm.profileId) {
      setError(t('teachers.profileRequired'));
      return;
    }

    try {
      await teacherService.createTeacher({
        userName: createForm.userName,
        firstName: createForm.firstName,
        lastName: createForm.lastName,
        profileId: createForm.profileId,
        enabled: createForm.enabled,
      });
      await loadTeachers(0, true);
      handleCancelCreate();
      setError('');
    } catch (e: any) {
      setError(t('teachers.failedToCreate') + ': ' + (e.response?.data?.message || e.message));
    }
  };

  const handleSave = async () => {
    if (!editingTeacher) return;
    if (!editForm.userName.trim()) {
      setError(t('teachers.userNameRequired'));
      return;
    }
    if (!editForm.profileId) {
      setError(t('teachers.profileRequired'));
      return;
    }

    try {
      await teacherService.updateTeacher(editingTeacher.id, {
        userName: editForm.userName,
        firstName: editForm.firstName,
        lastName: editForm.lastName,
        profileId: editForm.profileId,
        enabled: editForm.enabled,
      });
      await loadTeachers(0, true);
      handleCancelEdit();
      setError('');
    } catch (e: any) {
      setError(t('teachers.failedToUpdate') + ': ' + (e.response?.data?.message || e.message));
    }
  };

  const handleDelete = (id: string) => {
    showConfirm(t('common.delete'), t('teachers.confirmDelete'), async () => {
      try {
        await teacherService.deleteTeacher(id);
        await loadTeachers(0, true);
        setError('');
      } catch (e: any) {
        setError(t('teachers.failedToDelete') + ': ' + (e.response?.data?.message || e.message));
        showAlert(t('common.error'), t('teachers.failedToDelete'));
      }
    });
  };

  const handleResetPassword = (teacher: Teacher) => {
    setResetPasswordTeacher(teacher);
    setResetPasswordValue('');
  };

  const handleCancelResetPassword = () => {
    setResetPasswordTeacher(null);
    setResetPasswordValue('');
  };

  const handleSaveResetPassword = async () => {
    if (!resetPasswordTeacher) return;
    if (!resetPasswordValue.trim()) {
      setError(t('teachers.passwordRequired'));
      return;
    }

    try {
      await teacherService.resetPassword(resetPasswordTeacher.id, resetPasswordValue);
      handleCancelResetPassword();
      setError('');
      showAlert(t('common.success'), t('teachers.passwordResetSuccess'));
    } catch (e: any) {
      setError(t('teachers.failedToResetPassword') + ': ' + (e.response?.data?.message || e.message));
      showAlert(t('common.error'), t('teachers.failedToResetPassword'));
    }
  };

  if (loading && teachers.length === 0) {
    return <div className="bo-container">{t('common.loading')}</div>;
  }

  return (
    <div className="bo-container">
      <div className="bo-header">
        <div className="header-left">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="btn-toggle-sidebar"
            title={sidebarOpen ? t('common.hideFilters') : t('common.showFilters')}
          >
            {sidebarOpen ? '◀' : '▶'}
          </button>
          <h2>{t('teachers.title')}</h2>
        </div>
        {hasRight('teachers:c') && (
          <button onClick={handleCreate} className="btn-create">
            {t('teachers.createNew')}
          </button>
        )}
      </div>

      <div className="bo-content-wrapper">
        {sidebarOpen && (
          <div className="bo-sidebar">
            <div className="sidebar-header">
              <h3>{t('teachers.filters')}</h3>
              <div className="sidebar-header-actions">
                <button onClick={handleDeepSearch} className="btn-deep-search">
                  {t('teachers.deepSearch')}
                </button>
                <button onClick={clearFilters} className="btn-clear-filters">
                  {t('common.clear')}
                </button>
              </div>
            </div>
            {isDeepSearch && (
              <div className="deep-search-indicator">
                <small>{t('teachers.deepSearchActive')} ({totalElements} {t('teachers.results')})</small>
              </div>
            )}
            <div className="sidebar-body">
              <div className="filter-group">
                <label>{t('teachers.userName')}</label>
                <input
                  type="text"
                  value={filters.userName || ''}
                  onChange={(e) => handleFilterChange('userName', e.target.value)}
                  placeholder={t('teachers.filterByUserName')}
                />
              </div>
              <div className="filter-group">
                <label>{t('teachers.firstName')}</label>
                <input
                  type="text"
                  value={filters.firstName || ''}
                  onChange={(e) => handleFilterChange('firstName', e.target.value)}
                  placeholder={t('teachers.filterByFirstName')}
                />
              </div>
              <div className="filter-group">
                <label>{t('teachers.lastName')}</label>
                <input
                  type="text"
                  value={filters.lastName || ''}
                  onChange={(e) => handleFilterChange('lastName', e.target.value)}
                  placeholder={t('teachers.filterByLastName')}
                />
              </div>
              <div className="filter-group">
                <label>{t('teachers.enabled')}</label>
                <select
                  value={filters.enabled === true ? 'true' : filters.enabled === false ? 'false' : ''}
                  onChange={(e) => handleFilterChange('enabled', e.target.value === 'true' ? true : e.target.value === 'false' ? false : undefined)}
                >
                  <option value="">{t('teachers.all')}</option>
                  <option value="true">{t('common.yes')}</option>
                  <option value="false">{t('common.no')}</option>
                </select>
              </div>
            </div>
          </div>
        )}

        <div className={`bo-main-content ${!sidebarOpen ? 'full-width' : ''}`}>
          {error && <div className="error-message">{error}</div>}

          {isCreating && (
            <div className="modal-overlay" onClick={handleCancelCreate}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h3>{t('teachers.createTeacher')}</h3>
                </div>
                <div className="modal-body">
                  <div className="form-group">
                    <label>{t('teachers.userName')} *</label>
                    <input
                      type="text"
                      value={createForm.userName}
                      onChange={(e) => setCreateForm({ ...createForm, userName: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>{t('teachers.firstName')}</label>
                      <input
                        type="text"
                        value={createForm.firstName}
                        onChange={(e) => setCreateForm({ ...createForm, firstName: e.target.value })}
                      />
                    </div>
                    <div className="form-group">
                      <label>{t('teachers.lastName')}</label>
                      <input
                        type="text"
                        value={createForm.lastName}
                        onChange={(e) => setCreateForm({ ...createForm, lastName: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>{t('teachers.profile')} *</label>
                    <select
                      value={createForm.profileId}
                      onChange={(e) => setCreateForm({ ...createForm, profileId: e.target.value })}
                      required
                    >
                      <option value="">{t('teachers.selectProfile')}</option>
                      {teacherProfiles.map((profile) => (
                        <option key={profile.id} value={profile.id}>
                          {profile.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label style={{ display: 'flex', flexDirection: 'row-reverse' }}>
                      <input
                        type="checkbox"
                        checked={createForm.enabled}
                        onChange={(e) => setCreateForm({ ...createForm, enabled: e.target.checked })}
                      />
                      {t('teachers.enabled')}
                    </label>
                  </div>
                </div>
                <div className="modal-footer">
                  <button onClick={handleSaveCreate} className="btn-primary">{t('common.create')}</button>
                  <button onClick={handleCancelCreate} className="btn-secondary">{t('common.cancel')}</button>
                </div>
              </div>
            </div>
          )}

          {editingTeacher && (
            <div className="modal-overlay" onClick={handleCancelEdit}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h3>{t('teachers.editTeacher')}</h3>
                </div>
                <div className="modal-body">
                  <div className="form-group">
                    <label>{t('teachers.userName')} *</label>
                    <input
                      type="text"
                      value={editForm.userName}
                      onChange={(e) => setEditForm({ ...editForm, userName: e.target.value })}
                      required
                      disabled
                    />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>{t('teachers.firstName')}</label>
                      <input
                        type="text"
                        value={editForm.firstName}
                        onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })}
                      />
                    </div>
                    <div className="form-group">
                      <label>{t('teachers.lastName')}</label>
                      <input
                        type="text"
                        value={editForm.lastName}
                        onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>{t('teachers.profile')} *</label>
                    <select
                      value={editForm.profileId}
                      onChange={(e) => setEditForm({ ...editForm, profileId: e.target.value })}
                      required
                    >
                      <option value="">{t('teachers.selectProfile')}</option>
                      {teacherProfiles.map((profile) => (
                        <option key={profile.id} value={profile.id}>
                          {profile.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label style={{ display: 'flex', flexDirection: 'row-reverse' }}>
                      <input
                        type="checkbox"
                        checked={editForm.enabled}
                        onChange={(e) => setEditForm({ ...editForm, enabled: e.target.checked })}
                      />
                      {t('teachers.enabled')}
                    </label>
                  </div>
                </div>
                <div className="modal-footer">
                  <button onClick={handleSave} className="btn-primary">{t('common.save')}</button>
                  <button onClick={handleCancelEdit} className="btn-secondary">{t('common.cancel')}</button>
                </div>
              </div>
            </div>
          )}

          {resetPasswordTeacher && (
            <div className="modal-overlay" onClick={handleCancelResetPassword}>
              <div className="form-card" onClick={(e) => e.stopPropagation()}>
                <div className="form-header">
                  <h3>{t('teachers.resetPassword')} - {resetPasswordTeacher.userName}</h3>
                </div>
                <div className="form-body">
                  <div className="form-group">
                    <label>{t('teachers.newPassword')} *</label>
                    <input
                      type="password"
                      value={resetPasswordValue}
                      onChange={(e) => setResetPasswordValue(e.target.value)}
                      placeholder={t('teachers.newPasswordPlaceholder')}
                      required
                    />
                  </div>
                  <div className="form-actions">
                    <button onClick={handleSaveResetPassword} className="btn-primary">{t('common.save')}</button>
                    <button onClick={handleCancelResetPassword} className="btn-secondary">{t('common.cancel')}</button>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="bo-table-container">
            {filteredTeachers.length === 0 && !loading ? (
              <div className="no-data-message">
                <p>{t('teachers.noTeachersFound')}</p>
              </div>
            ) : (
              <>
                <div className="bo-table-wrapper" ref={observerTarget}>
                  <table className="bo-table">
                    <thead>
                      <tr>
                        <th>{t('teachers.userName')}</th>
                        <th>{t('teachers.firstName')}</th>
                        <th>{t('teachers.lastName')}</th>
                        <th>{t('teachers.enabled')}</th>
                        {hasAnyActionRight && <th>{t('common.actions')}</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredTeachers.map((teacher) => (
                        <tr key={teacher.id}>
                          <td>{teacher.userName}</td>
                          <td>{teacher.firstName || '-'}</td>
                          <td>{teacher.lastName || '-'}</td>
                          <td>{teacher.enabled ? t('common.yes') : t('common.no')}</td>
                          {hasAnyActionRight && (
                            <td>
                              {hasAnyActionRight && (
                                <td>
                                  <TableActions actions={[
                                    ...(hasRight('teachers:u') ? [{
                                      label: t('common.edit'),
                                      onClick: () => handleEdit(teacher),
                                      className: 'btn-edit',
                                      icon: <Pencil size={16} />,
                                      hideLabelDesktop: true
                                    }] : []),
                                    ...(hasRight('teachers:u') ? [{
                                      label: t('teachers.resetPassword'),
                                      onClick: () => handleResetPassword(teacher),
                                      className: 'btn-reset-password',
                                      icon: <Lock size={16} />,
                                      hideLabelDesktop: true
                                    }] : []),
                                    ...(hasRight('teachers:d') ? [{
                                      label: t('common.delete'),
                                      onClick: () => handleDelete(teacher.id),
                                      className: 'btn-delete',
                                      icon: <Trash2 size={16} />,
                                      hideLabelDesktop: true,
                                      variant: 'danger' as const
                                    }] : []),
                                    ...(hasRight('bills:r') ? [{
                                      label: t('teachers.viewBills'),
                                      onClick: () => navigate(`/bills?resourceType=${ResourceType.TEACHER}&resourceId=${teacher.id}`),
                                      className: 'btn-bills',
                                      icon: <Receipt size={16} />,
                                      hideLabelDesktop: true
                                    }] : [])
                                  ]} />
                                </td>
                              )}
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="scroll-trigger">
                    {loadingMore && (
                      <div className="loading-more">
                        <small>{t('common.loading')}...</small>
                      </div>
                    )}
                    {!hasMore && teachers.length > 0 && (
                      <div className="no-more-data">
                        <small>{t('teachers.allLoaded')}</small>
                      </div>
                    )}
                  </div>
                </div>
                {!isDeepSearch && (
                  <div className="table-info">
                    <small>{t('teachers.showingLoaded')} ({teachers.length} {t('teachers.loaded')})</small>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Teachers;
